/**
 * Targeting solutions: terminal-dive and ballistic-release geometry.
 *
 * These endpoints only compute — they return coordinates, numbers and a draft
 * `mission` for the operator to review. Nothing here touches the vehicle
 * registry, exactly like the AI duty officer in api/ai.ts: a solution can be
 * drawn and drafted, never flown. The DRAFT-is-not-FLY boundary lives in the
 * caller (AttackDock drafts via missionStore; it never uploads).
 *
 * Written against the backend wire contract in api/app/domain/dive_attack.py and
 * release.py. Latency is milliseconds, not the AI's seconds, so the default
 * request timeout is fine.
 */

import { api, type ApiResult } from '@/api/client';
import type { Waypoint } from '@/types/domain';

export type AttackQuality = 'marginal' | 'acceptable' | 'good';

/** Optional airframe overrides. Omitted fields fall back to backend defaults. */
export interface Airframe {
  massKg: number;
  cruiseSpeedMs: number;
  maxDiveSpeedMs: number;
  dragTerminalSpeedMs: number;
  maxStructuralAngleDeg: number;
}

/** Optional released-store aerodynamics. Omitted fields fall back to defaults. */
export interface Store {
  massKg: number;
  dragCoefficient: number;
  crossSectionM2: number;
  terminalVelocityMs: number;
}

/** Per-vehicle release-mechanism wiring. Advisory only — it never actuates. */
export interface Dropper {
  servoChannel: number;
  openPwm: number;
  closedPwm: number;
}

/** Wind at the target. Null/omitted means the solution is left uncorrected. */
export interface WindConditions {
  windSpeedMs: number;
  windFromDeg: number;
}

export interface DiveRequest {
  targetLat: number;
  targetLon: number;
  pushoverAltM: number;
  diveAngleDeg: number;
  approachBearingDeg: number;
  airframe?: Partial<Airframe>;
  conditions?: WindConditions | null;
}

export interface DiveSolution {
  pushoverLat: number;
  pushoverLon: number;
  pushoverAltM: number;
  aimLat: number;
  aimLon: number;
  targetLat: number;
  targetLon: number;
  diveAngleDeg: number;
  runBearingDeg: number;
  slantRangeM: number;
  groundRangeM: number;
  heightDropM: number;
  impactSpeedMs: number;
  impactEnergyJ: number;
  timeToImpactS: number;
  timingSensitivityM: number;
  windCorrectionM: number;
  quality: AttackQuality;
  notes: string[];
  mission: Waypoint[];
}

export interface DiveAnglesRequest {
  pushoverAltM: number;
  groundRangeM: number;
  airframe?: Partial<Airframe>;
}

/** Three pickable dive angles: gentle, best-tradeoff, and steep-terminal. */
export interface DiveAngles {
  shallow: number;
  optimal: number;
  steep: number;
}

export interface ReleaseRequest {
  targetLat: number;
  targetLon: number;
  releaseAltM: number;
  groundSpeedMs: number;
  trackBearingDeg: number;
  store?: Partial<Store>;
  dropper?: Partial<Dropper>;
  conditions?: WindConditions | null;
  descentRateMs?: number;
}

export interface ReleaseSolution {
  releaseLat: number;
  releaseLon: number;
  releaseAltM: number;
  targetLat: number;
  targetLon: number;
  runBearingDeg: number;
  releaseLeadM: number;
  fallTimeS: number;
  forwardThrowM: number;
  impactSpeedMs: number;
  impactEnergyJ: number;
  timingSensitivityM: number;
  windCorrectionM: number;
  quality: AttackQuality;
  notes: string[];
  mission: Waypoint[];
}

export function planDive(body: DiveRequest): Promise<ApiResult<DiveSolution>> {
  return api.post<DiveSolution>('/planner/dive', body, { label: 'PLAN DIVE' });
}

export function suggestDiveAngles(
  body: DiveAnglesRequest,
): Promise<ApiResult<DiveAngles>> {
  return api.post<DiveAngles>('/planner/dive-angles', body, {
    label: 'DIVE ANGLES',
    logged: false,
  });
}

export function planRelease(body: ReleaseRequest): Promise<ApiResult<ReleaseSolution>> {
  return api.post<ReleaseSolution>('/planner/release', body, { label: 'PLAN RELEASE' });
}
